import { ref, computed, useId, watch, onBeforeUnmount } from 'vue'
import {
  useFloating,
  offset as offsetFunc,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/vue'

import type { AppTooltipProps } from '@UI/Tooltips/AppTooltip/types/interfaces'

export const useAppTooltip = (props: AppTooltipProps) => {
  const tooltipId = useId()

  const isVisible = ref(false)
  const isRendered = ref(false)
  const triggerRef = ref<HTMLElement | null>(null)
  const tooltipRef = ref<HTMLElement | null>(null)

  const tooltipState = computed(() => (isVisible.value ? 'open' : 'closed'))
  const show = computed(() => isRendered.value && !!props.text)

  let showTimeout: ReturnType<typeof setTimeout> | null = null
  let hideTimeout: ReturnType<typeof setTimeout> | null = null

  // --- Floating UI ---

  const { floatingStyles, placement: resolvedPlacement } = useFloating(
    triggerRef,
    tooltipRef,
    {
      placement: computed(() => props.placement ?? 'bottom'),
      strategy: 'fixed',
      whileElementsMounted: autoUpdate,
      middleware: computed(() => [
        offsetFunc(props.offset ?? 4),
        flip(),
        shift({ padding: 8 }),
      ]),
    },
  )

  const resolvedSide = computed(() => resolvedPlacement.value.split('-')[0])

  // --- Teleport ---

  const teleportTarget = computed(() => {
    if (props.teleportTo === false) return 'body'
    if (!props.teleportTo) return 'body'

    if (props.teleportTo !== 'body' && !document.querySelector(props.teleportTo)) {
      console.warn(
        `[AppTooltip] Teleport target "${props.teleportTo}" not found in DOM, falling back to "body".`,
      )

      return 'body'
    }

    return props.teleportTo
  })

  // --- Helpers ---

  const clearTimeouts = (): void => {
    if (showTimeout) {
      clearTimeout(showTimeout)
      showTimeout = null
    }
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      hideTimeout = null
    }
  }

  const isTriggerDisabled = (): boolean => {
    if (!triggerRef.value) return false

    const target = triggerRef.value.firstElementChild as HTMLElement | null

    if (!target) return false

    return (
      target.hasAttribute('disabled')
      || target.classList.contains('disabled')
    )
  }

  // --- Visibility ---

  const hideImmediate = (): void => {
    clearTimeouts()
    isVisible.value = false
    isRendered.value = false
  }

  const showTooltip = (): void => {
    if (props.mode === 'disabled' && !isTriggerDisabled()) return

    clearTimeouts()

    showTimeout = setTimeout(() => {
      isVisible.value = true
      isRendered.value = true
    }, props.delay ?? 100)
  }

  const hideTooltip = (): void => {
    clearTimeouts()

    hideTimeout = setTimeout(() => {
      isVisible.value = false
    }, 100)
  }

  const toggleTooltip = (): void => {
    if (isVisible.value) {
      hideTooltip()
    } else {
      showTooltip()
    }
  }

  const handleAnimationEnd = (): void => {
    if (!isVisible.value) {
      isRendered.value = false
    }
  }

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && isVisible.value) {
      isVisible.value = false
    }
  }

  // --- Event listeners ---

  const handleScroll = (event: Event): void => {
    const target = event.target as HTMLElement | null

    if (target?.contains(triggerRef.value)) {
      hideImmediate()
    }
  }

  const handleTouchOutside = (event: TouchEvent): void => {
    if (!triggerRef.value?.contains(event.target as Node)) {
      hideTooltip()
    }
  }

  const startListeners = (): void => {
    window.addEventListener('scroll', handleScroll, true)
    document.addEventListener('touchstart', handleTouchOutside)
  }

  const stopListeners = (): void => {
    window.removeEventListener('scroll', handleScroll, true)
    document.removeEventListener('touchstart', handleTouchOutside)
  }

  // --- Lifecycle ---

  watch(isVisible, (visible) => {
    if (visible) {
      startListeners()
    } else {
      stopListeners()
    }
  })

  watch(() => props.text, (text) => {
    if (!text) hideImmediate()
  })

  onBeforeUnmount(() => {
    clearTimeouts()
    stopListeners()
  })

  return {
    tooltipId,
    triggerRef,
    tooltipRef,
    show,
    tooltipState,
    floatingStyles,
    resolvedPlacement,
    resolvedSide,
    teleportTarget,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    handleAnimationEnd,
    handleKeydown,
  }
}
